import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseSimulationTimerOptions {
  initialSeconds?: number;
  onExpire?: () => void;
  isPaused?: boolean;
}

export function useSimulationTimer({
  initialSeconds = 600,
  onExpire,
  isPaused = false,
}: UseSimulationTimerOptions) {
  const [secondsRemaining, setSecondsRemaining] = useState<number>(initialSeconds);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const hasExpiredRef = useRef(false);

  // Reset if initial duration changes
  useEffect(() => {
    hasExpiredRef.current = false;
    setSecondsRemaining(initialSeconds);
  }, [initialSeconds]);

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused]);

  // Fire onExpire exactly once, outside the state updater. Calling it from
  // inside the updater double-fires under React 18 StrictMode and can race
  // with a manual "End Simulation" action.
  useEffect(() => {
    if (secondsRemaining > 0 || hasExpiredRef.current) return;
    hasExpiredRef.current = true;
    if (onExpireRef.current) {
      onExpireRef.current();
    }
  }, [secondsRemaining]);

  const formatTimer = useCallback((totalSeconds?: number): string => {
    const s = totalSeconds !== undefined ? totalSeconds : secondsRemaining;
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, [secondsRemaining]);

  const isLowTime = secondsRemaining > 0 && secondsRemaining <= 60;

  return {
    secondsRemaining,
    setSecondsRemaining,
    formattedTime: formatTimer(secondsRemaining),
    formatTimer,
    isLowTime,
  };
}
